/* eslint-disable react-refresh/only-export-components */
/**
 * A small set of inline SVG icons (stroke = currentColor, 20x20 box). No icon library: the app
 * needs a dozen glyphs and they must follow the theme through `currentColor`.
 */
import type { ReactNode } from 'react';

function Svg({ children }: { children: ReactNode }) {
  return (
    <svg
      width="1.25em"
      height="1.25em"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export const Icons = {
  bell: () => (
    <Svg>
      <path d="M5 8a5 5 0 0 1 10 0c0 4 1.5 5 1.5 5h-13S5 12 5 8ZM8.5 16a1.6 1.6 0 0 0 3 0" />
    </Svg>
  ),
  users: () => (
    <Svg>
      <circle cx="7.5" cy="7" r="2.6" />
      <path d="M2.5 16c0-2.6 2.2-4.5 5-4.5s5 1.9 5 4.5M13 4.6a2.6 2.6 0 0 1 0 4.8M14.5 11.8c1.7.5 3 2 3 4.2" />
    </Svg>
  ),
  share: () => (
    <Svg>
      <circle cx="5" cy="10" r="2" />
      <circle cx="15" cy="5" r="2" />
      <circle cx="15" cy="15" r="2" />
      <path d="m6.8 9 6.4-3M6.8 11l6.4 3" />
    </Svg>
  ),
  link: () => (
    <Svg>
      <path d="M8.5 11.5a3 3 0 0 0 4.2 0l2.8-2.8a3 3 0 0 0-4.2-4.2l-.8.8M11.5 8.5a3 3 0 0 0-4.2 0L4.5 11.3a3 3 0 0 0 4.2 4.2l.8-.8" />
    </Svg>
  ),
  lock: () => (
    <Svg>
      <rect x="4.5" y="9" width="11" height="7.5" rx="1.5" />
      <path d="M7 9V6.5a3 3 0 0 1 6 0V9" />
    </Svg>
  ),
  inbox: () => (
    <Svg>
      <path d="M3 11.5 5 4.5h10l2 7M3 11.5v4h14v-4M3 11.5h4l1 2h4l1-2h4" />
    </Svg>
  ),
  download: () => (
    <Svg>
      <path d="M10 3v9m0 0-3.5-3.5M10 12l3.5-3.5M4 15.5h12" />
    </Svg>
  ),
  upload: () => (
    <Svg>
      <path d="M10 13V4m0 0L6.5 7.5M10 4l3.5 3.5M4 15.5h12" />
    </Svg>
  ),
  // Portability: the same glyphs by meaning. Import = arrow DOWN onto a line (data comes in),
  // export = arrow UP off a line (data goes out); file pickers and file downloads follow it.
  import: () => Icons.download(),
  export: () => Icons.upload(),
  settings: () => (
    <Svg>
      <circle cx="10" cy="10" r="2.6" />
      <path d="M10 2.5v2M10 15.5v2M2.5 10h2M15.5 10h2M4.7 4.7l1.4 1.4M13.9 13.9l1.4 1.4M4.7 15.3l1.4-1.4M13.9 6.1l1.4-1.4" />
    </Svg>
  ),
  palette: () => (
    <Svg>
      <path d="M10 3a7 7 0 1 0 0 14c1.1 0 1.6-.8 1.3-1.7-.3-.9.2-1.8 1.2-1.8H14a3 3 0 0 0 3-3C17 5.9 13.9 3 10 3Z" />
      <circle cx="6.5" cy="9" r=".7" fill="currentColor" />
      <circle cx="9" cy="6.2" r=".7" fill="currentColor" />
      <circle cx="12.5" cy="6.8" r=".7" fill="currentColor" />
    </Svg>
  ),
  more: () => (
    <Svg>
      <circle cx="4.5" cy="10" r="1" fill="currentColor" />
      <circle cx="10" cy="10" r="1" fill="currentColor" />
      <circle cx="15.5" cy="10" r="1" fill="currentColor" />
    </Svg>
  ),
  search: () => (
    <Svg>
      <circle cx="8.5" cy="8.5" r="5" />
      <path d="m12.5 12.5 4 4" />
    </Svg>
  ),
  tag: () => (
    <Svg>
      <path d="M3 3h6.2l7.3 7.3a1.4 1.4 0 0 1 0 2L12.3 16.5a1.4 1.4 0 0 1-2 0L3 9.2V3Z" />
      <circle cx="6.5" cy="6.5" r="1" fill="currentColor" />
    </Svg>
  ),
  check: () => (
    <Svg>
      <path d="m4.5 10.5 3.5 3.5 7.5-8" />
    </Svg>
  ),
  plus: () => (
    <Svg>
      <path d="M10 4v12M4 10h12" />
    </Svg>
  ),
  close: () => (
    <Svg>
      <path d="m5 5 10 10M15 5 5 15" />
    </Svg>
  ),
  star: () => (
    <Svg>
      <path d="m10 2.8 2.2 4.6 5 .7-3.6 3.5.9 5-4.5-2.4-4.5 2.4.9-5L2.8 8.1l5-.7L10 2.8Z" />
    </Svg>
  ),
  pin: () => (
    <Svg>
      <path d="M12.5 3.5 16.5 7.5l-2 .8-2.6 2.6.3 3.1-1 1-3-3-4 4M8.2 8.1 7 6.9l1-1 3.1.3L13.7 3.6" />
    </Svg>
  ),
  paperclip: () => (
    <Svg>
      <path d="m16 9.5-6 6a3.6 3.6 0 0 1-5.1-5.1l6.2-6.2a2.4 2.4 0 0 1 3.4 3.4L8.4 13.7a1.2 1.2 0 0 1-1.7-1.7l5.5-5.5" />
    </Svg>
  ),
  back: () => (
    <Svg>
      <path d="m11.5 4.5-5.5 5.5 5.5 5.5" />
    </Svg>
  ),
  board: () => (
    <Svg>
      <rect x="3" y="3.5" width="14" height="13" rx="2" />
      <path d="M8 3.5v13M12.5 3.5v8" />
    </Svg>
  ),
  trash: () => (
    <Svg>
      <path d="M4 5.5h12M8 5.5V4h4v1.5M5.5 5.5l.7 10h7.6l.7-10M8.5 8.5v4.5M11.5 8.5v4.5" />
    </Svg>
  ),
  copy: () => (
    <Svg>
      <rect x="7" y="7" width="9.5" height="9.5" rx="1.8" />
      <path d="M13 7V5.3A1.8 1.8 0 0 0 11.2 3.5H5.3A1.8 1.8 0 0 0 3.5 5.3v5.9A1.8 1.8 0 0 0 5.3 13H7" />
    </Svg>
  ),
  move: () => (
    <Svg>
      <path d="M3.5 10h12m0 0-4-4m4 4-4 4" />
    </Svg>
  ),
  arrowUp: () => (
    <Svg>
      <path d="M10 16V4.5m0 0-4.5 4.5M10 4.5 14.5 9" />
    </Svg>
  ),
  arrowDown: () => (
    <Svg>
      <path d="M10 4v11.5m0 0L5.5 11M10 15.5 14.5 11" />
    </Svg>
  ),
  archive: () => (
    <Svg>
      <rect x="3" y="4" width="14" height="3.5" rx="1" />
      <path d="M4.5 7.5V15a1.5 1.5 0 0 0 1.5 1.5h8A1.5 1.5 0 0 0 15.5 15V7.5M8 11h4" />
    </Svg>
  ),
  image: () => (
    <Svg>
      <rect x="3" y="4" width="14" height="12" rx="2" />
      <circle cx="7.5" cy="8.5" r="1.2" />
      <path d="m4 14.5 4-3.5 3 2.5 2-1.8 3 2.8" />
    </Svg>
  ),
  refresh: () => (
    <Svg>
      <path d="M16 10a6 6 0 1 1-1.8-4.3M16 3.5v3h-3" />
    </Svg>
  ),
  chevronDown: () => (
    <Svg>
      <path d="m5 7.5 5 5 5-5" />
    </Svg>
  ),
  chevronRight: () => (
    <Svg>
      <path d="m7.5 5 5 5-5 5" />
    </Svg>
  ),
  help: () => (
    <Svg>
      <circle cx="10" cy="10" r="7" />
      <path d="M7.9 8a2.2 2.2 0 1 1 3.2 2c-.7.4-1.1.8-1.1 1.5M10 14h.01" />
    </Svg>
  ),
  calendar: () => (
    <Svg>
      <rect x="3.5" y="4.5" width="13" height="12" rx="2" />
      <path d="M3.5 8.5h13M7 3v3M13 3v3" />
    </Svg>
  ),
  keyboard: () => (
    <Svg>
      <rect x="2.5" y="5.5" width="15" height="9" rx="2" />
      <path d="M5.5 8.5h.01M8.5 8.5h.01M11.5 8.5h.01M14.5 8.5h.01M6.5 11.5h7" />
    </Svg>
  ),
  user: () => (
    <Svg>
      <circle cx="10" cy="7" r="3" />
      <path d="M4 16.5c.6-3 3-4.5 6-4.5s5.4 1.5 6 4.5" />
    </Svg>
  ),
  note: () => (
    <Svg>
      <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h9A1.5 1.5 0 0 1 16 5.5v6.500a1.5 1.5 0 0 1-1.5 1.5H9l-3.500 3v-3H5.5A1.5 1.5 0 0 1 4 12V5.5Z" />
    </Svg>
  ),
  flag: () => (
    <Svg>
      <path d="M5 17V3.500M5 4h9l-2 3.500 2 3.500H5" />
    </Svg>
  ),
  clock: () => (
    <Svg>
      <circle cx="10" cy="10" r="7" />
      <path d="M10 6v4l2.5 1.5" />
    </Svg>
  ),
  plusCircle: () => (
    <Svg>
      <circle cx="10" cy="10" r="7" />
      <path d="M10 7v6M7 10h6" />
    </Svg>
  ),
  list: () => (
    <Svg>
      <path d="M7 5.5h9M7 10h9M7 14.5h9M4 5.5h.01M4 10h.01M4 14.5h.01" />
    </Svg>
  ),
  sort: () => (
    <Svg>
      <path d="M6 4v12m0 0-2.5-2.5M6 16l2.5-2.5M14 16V4m0 0-2.5 2.5M14 4l2.5 2.5" />
    </Svg>
  ),
  eye: () => (
    <Svg>
      <path d="M2.5 10S5 5 10 5s7.5 5 7.5 5-2.5 5-7.5 5-7.5-5-7.5-5Z" />
      <circle cx="10" cy="10" r="2.2" />
    </Svg>
  ),
  /** A warning triangle: "this becomes overdue". */
  alert: () => (
    <Svg>
      <path d="M10 3.500 17.500 16.500h-15L10 3.500Z" />
      <path d="M10 8.500v3.500M10 14.200v.1" />
    </Svg>
  ),
  eyeOff: () => (
    <Svg>
      <path d="M2.5 10S5 5 10 5c1.2 0 2.300.3 3.200.8M17.500 10s-2.500 5-7.500 5c-1.200 0-2.300-.3-3.200-.8" />
      <circle cx="10" cy="10" r="2.200" />
      <path d="m3.500 3.500 13 13" />
    </Svg>
  ),
  /** Bars of different heights: how much each day holds. */
  workload: () => (
    <Svg>
      <path d="M4.500 16V10M10 16V4.500M15.500 16v-4.500" />
    </Svg>
  ),
  edit: () => (
    <Svg>
      <path d="m4 16 .8-3.2L13.5 4a1.800 1.800 0 0 1 2.5 2.5l-8.700 8.700L4 16Z" />
    </Svg>
  ),
  layers: () => (
    <Svg>
      <path d="m10 3.5 7 3.5-7 3.5L3 7l7-3.5Z" />
      <path d="m3 10.5 7 3.5 7-3.5M3 14l7 3.5 7-3.5" />
    </Svg>
  ),
  /** Roles and permissions. */
  shield: () => (
    <Svg>
      <path d="M10 2.8 4.5 5v4.4c0 3.4 2.3 6 5.5 7.8 3.2-1.800 5.500-4.400 5.500-7.800V5L10 2.800Z" />
      <path d="m7.600 9.800 1.700 1.700 3.200-3.400" />
    </Svg>
  ),
  /** Audit log / history. */
  history: () => (
    <Svg>
      <path d="M3.500 10a6.500 6.500 0 1 0 2-4.700M3 3.500v3h3" />
      <path d="M10 6.500V10l2.500 1.500" />
    </Svg>
  ),
  /** Workspace settings / board settings sliders. */
  sliders: () => (
    <Svg>
      <path d="M3 6h7M14 6h3M3 14h3M10 14h7" />
      <circle cx="12" cy="6" r="2" />
      <circle cx="8" cy="14" r="2" />
    </Svg>
  ),
  /** Who may open the board: people with a key. */
  key: () => (
    <Svg>
      <circle cx="7" cy="12.500" r="3.500" />
      <path d="m9.500 10 6.500-6.500M13.500 5.500l2 2M12 7l1.500 1.500" />
    </Svg>
  ),
  /** Switch between boards. */
  switcher: () => (
    <Svg>
      <rect x="3" y="3.500" width="6" height="6" rx="1.200" />
      <rect x="11" y="3.500" width="6" height="6" rx="1.200" />
      <rect x="3" y="11.500" width="6" height="5" rx="1.200" />
      <path d="M12 14h5M15 12l2 2-2 2" />
    </Svg>
  ),
  arrowRight: () => (
    <Svg>
      <path d="M4 10h11M11 6l4 4-4 4" />
    </Svg>
  ),
  chevronLeft: () => (
    <Svg>
      <path d="m12 5-5 5 5 5" />
    </Svg>
  ),
  /** The workspace (an organisation / building). */
  workspace: () => (
    <Svg>
      <path d="M4 17V4.500a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1V17M12 8.500h3a1 1 0 0 1 1 1V17M2.500 17h15" />
      <path d="M7 7h2M7 10h2M7 13h2" />
    </Svg>
  ),
  crown: () => (
    <Svg>
      <path d="m3 6.500 3.500 3.500L10 5l3.500 5L17 6.500 15.500 15h-11L3 6.500Z" />
    </Svg>
  ),
  userCheck: () => (
    <Svg>
      <circle cx="8" cy="7" r="2.800" />
      <path d="M2.500 16c0-2.800 2.400-4.600 5.500-4.600M12 14l1.800 1.800L17 12.500" />
    </Svg>
  ),
  mail: () => (
    <Svg>
      <rect x="3" y="4.500" width="14" height="11" rx="1.500" />
      <path d="m3.500 6 6.500 5 6.500-5" />
    </Svg>
  ),
  /* --- database field types and grid actions --- */
  fieldText: () => (
    <Svg>
      <path d="M4 5.5V4h12v1.5M10 4v12M7.5 16h5" />
    </Svg>
  ),
  fieldLongText: () => (
    <Svg>
      <path d="M3.5 5h13M3.5 8.5h13M3.5 12h13M3.5 15.5h8" />
    </Svg>
  ),
  fieldNumber: () => (
    <Svg>
      <path d="M7.5 3.5 6 16.5M14 3.5l-1.500 13M3.5 7.500h13M3.500 12.500h13" />
    </Svg>
  ),
  fieldCheckbox: () => (
    <Svg>
      <rect x="3.5" y="3.5" width="13" height="13" rx="2.5" />
      <path d="m7 10.200 2.200 2.300L13.200 7.500" />
    </Svg>
  ),
  fieldSelect: () => (
    <Svg>
      <circle cx="10" cy="10" r="7" />
      <path d="m7.500 9 2.500 2.500L12.500 9" />
    </Svg>
  ),
  fieldMultiSelect: () => (
    <Svg>
      <path d="M3.500 5.500 5 7l2-2.500M3.500 12.500 5 14l2-2.500M10 6h6.500M10 13h6.500" />
    </Svg>
  ),
  fieldStatus: () => (
    <Svg>
      <circle cx="10" cy="10" r="7" />
      <circle cx="10" cy="10" r="2.500" fill="currentColor" />
    </Svg>
  ),
  wrap: () => (
    <Svg>
      <path d="M3.500 5h13M3.500 10h10a2.500 2.500 0 0 1 0 5H11m0 0 1.800-1.800M11 15l1.800 1.800M3.500 15H7" />
    </Svg>
  ),
  freeze: () => (
    <Svg>
      <rect x="3.500" y="3.500" width="13" height="13" rx="2" />
      <path d="M8.500 3.500v13" strokeWidth="2.400" />
    </Svg>
  ),
  fill: () => (
    <Svg>
      <path d="m4.500 10 5-5.500 5.500 5.500-5 5-5.500-5ZM9.500 4.500 7 2M16.500 13.500c0 1.100-.8 2-1.500 2s-1.500-.9-1.500-2c0-1 1.500-2.500 1.500-2.500s1.500 1.500 1.500 2.500Z" />
    </Svg>
  ),
  alignLeft: () => (
    <Svg>
      <path d="M3.500 5h13M3.500 9h8M3.500 13h13M3.500 17h8" />
    </Svg>
  ),
  alignCenter: () => (
    <Svg>
      <path d="M3.500 5h13M6 9h8M3.500 13h13M6 17h8" />
    </Svg>
  ),
  alignRight: () => (
    <Svg>
      <path d="M3.500 5h13M8.500 9h8M3.500 13h13M8.500 17h8" />
    </Svg>
  ),
  alignTop: () => (
    <Svg>
      <path d="M3.500 3.500h13M10 7v9.500M6.500 10.500 10 7l3.500 3.500" />
    </Svg>
  ),
  alignMiddle: () => (
    <Svg>
      <path d="M3.500 10h13M10 3.500v4M7.500 5.500 10 8l2.500-2.500M10 16.500v-4M7.500 14.500 10 12l2.500 2.500" />
    </Svg>
  ),
  alignBottom: () => (
    <Svg>
      <path d="M3.500 16.500h13M10 13V3.500M6.500 9.500 10 13l3.500-3.500" />
    </Svg>
  ),
  textFormat: () => (
    <Svg>
      <path d="m3 15 4-10 4 10M4.500 11.500h5M12.500 15l2.500-6 2.500 6M13.300 13h3.400" />
    </Svg>
  ),
  textColor: () => (
    <Svg>
      <path d="m5 14 5-11 5 11M6.800 10h6.400" />
    </Svg>
  ),
  eraser: () => (
    <Svg>
      <path d="m8 16.500-4-4a1.500 1.500 0 0 1 0-2.100l6-6a1.500 1.500 0 0 1 2.100 0l4 4a1.500 1.500 0 0 1 0 2.100l-5.500 5.500M8 16.500h8M6.500 8.500l5 5" />
    </Svg>
  ),
  /** A house (the Home hub). */
  home: () => (
    <Svg>
      <path d="m3 9.500 7-6 7 6M5 8.500V16a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V8.500M8.500 17v-4.500h3V17" />
    </Svg>
  ),
  rows: () => (
    <Svg>
      <rect x="3.500" y="4" width="13" height="4" rx="1" />
      <rect x="3.500" y="12" width="13" height="4" rx="1" />
    </Svg>
  ),
  /** View modes of a table (Lucide-style: `table-2`, `layout-grid`, `square-kanban`). */
  viewTable: () => (
    <Svg>
      <rect x="3" y="4" width="14" height="12" rx="1.500" />
      <path d="M3 8.500h14M3 12.500h14M8.500 8.500V16" />
    </Svg>
  ),
  viewCards: () => (
    <Svg>
      <rect x="3.500" y="3.500" width="5.500" height="5.500" rx="1" />
      <rect x="11" y="3.500" width="5.500" height="5.500" rx="1" />
      <rect x="3.500" y="11" width="5.500" height="5.500" rx="1" />
      <rect x="11" y="11" width="5.500" height="5.500" rx="1" />
    </Svg>
  ),
  viewKanban: () => (
    <Svg>
      <rect x="3" y="3.500" width="14" height="13" rx="1.500" />
      <path d="M7 7v5M10 7v2.500M13 7v7" />
    </Svg>
  ),
  /** A drag handle: two columns of dots. */
  grip: () => (
    <Svg>
      <circle cx="7.500" cy="5.500" r=".7" />
      <circle cx="12.500" cy="5.500" r=".7" />
      <circle cx="7.500" cy="10" r=".7" />
      <circle cx="12.500" cy="10" r=".7" />
      <circle cx="7.500" cy="14.500" r=".7" />
      <circle cx="12.500" cy="14.500" r=".7" />
    </Svg>
  ),
  /** A section (Lucide-style `folder`). */
  folder: () => (
    <Svg>
      <path d="M3 6.500a1.500 1.500 0 0 1 1.500-1.500h3.200l1.600 1.800h6.200A1.500 1.500 0 0 1 17 8.300v6.200a1.500 1.500 0 0 1-1.500 1.500h-11A1.500 1.500 0 0 1 3 14.500Z" />
    </Svg>
  ),
  /** The Feedback Center entry (a lightbulb). */
  lightbulb: () => (
    <Svg>
      <path d="M7.500 14.500v-1.100c0-.5-.2-.9-.6-1.300A5 5 0 1 1 13.100 12.100c-.4.400-.6.800-.6 1.300v1.100M7.800 16.500h4.400M8.700 18.200h2.600" />
    </Svg>
  ),
  /** The compact navigation menu (three lines). */
  menu: () => (
    <Svg>
      <path d="M3.500 5.500h13M3.500 10h13M3.500 14.500h13" />
    </Svg>
  ),
  /** Show the whole content (two arrows pointing apart). */
  expand: () => (
    <Svg>
      <path d="M11.500 3.500h5v5M16.500 3.500l-5.200 5.200M8.500 16.500h-5v-5M3.500 16.500l5.200-5.200" />
    </Svg>
  ),
  /** A note: a document page with lines of text. */
  document: () => (
    <Svg>
      <path d="M5 2.800h6.200L15.500 7.100v9.200a1 1 0 0 1-1 1h-9.500a1 1 0 0 1-1-1V3.800a1 1 0 0 1 1-1ZM11 2.800V7.400h4.500M7 10.500h6M7 13.500h6" />
    </Svg>
  ),
  /** A group: several things held together. */
  group: () => (
    <Svg>
      <path d="M3.500 4.500h5v5h-5ZM11.500 4.500h5v5h-5ZM3.500 12h5v4.500h-5ZM11.500 12h5v4.500h-5Z" />
    </Svg>
  ),
  /** A plain file. */
  file: () => (
    <Svg>
      <path d="M5 2.800h6.200L15.500 7.100v9.200a1 1 0 0 1-1 1h-9.500a1 1 0 0 1-1-1V3.800a1 1 0 0 1 1-1ZM11 2.800V7.400h4.500" />
    </Svg>
  ),
} as const;
